import React, { useState } from "react";
import { useParams } from "react-router-dom";
import adminApi from "@diditusa/shared/api/adminApi";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Box,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import ImageUploadInput from "../../components/ImageUploadInput/ImageUploadInput";
import { ImagePurpose, ImageEntity } from "@diditusa/shared/types/uploadsTypes";
import { Button } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import LoadingIndicator from "@diditusa/shared/web_components/LoadingIndicator/loadingIndicator";
import { getErrorMessage } from "@diditusa/shared/api/apiUtils";
import { green } from "@mui/material/colors";

const StyledCard = styled(Card)({
  border: "1px solid #e0e0e0",
  borderRadius: 8,
});

const Item = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  display: "flex",
  alignItems: "center",
}));

const UserDetails = () => {
  const { userId } = useParams();
  const {
    data: user,
    isLoading,
    error,
  } = adminApi.useAdminGetUserQuery(userId!);
  const [deleteValidationImage, deleteValidationImageState] =
    adminApi.useDeleteUserValidationImageMutation();
  const [updateUser, updateUserState] = adminApi.useAdminUpdateUserMutation();
  const [newFileIds, setNewFileIds] = useState<number[]>([]);

  const getOnDeleteImageClick = (fileId: number) => {
    if (!userId) {
      return;
    }
    return () => {
      deleteValidationImage({ userId, fileId });
    };
  };

  const onSetIdentityVerifiedClick = () => {
    if (!userId) {
      return;
    }
    updateUser({
      userId,
      request: { identityVerified: !user?.identityVerified },
    });
  };

  if (isLoading) {
    return <p>Loading user details...</p>;
  }

  if (error) {
    return <p>Error: {(error as any).data.message}</p>;
  }

  if (!user) {
    return <p>User not found.</p>;
  }

  return (
    <Box p="16px">
      <StyledCard>
        <CardHeader
          avatar={<Avatar src={user.imageUrl} />}
          title={
            <Typography
              variant="h6"
              fontWeight="bold"
              textAlign="left"
            >{`${user.firstName} ${user.lastName}`}</Typography>
          }
          subheader={
            <Typography variant="subtitle2" textAlign="left">
              User Role
            </Typography>
          }
        />
        <CardContent>
          <Grid
            container
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          >
            <Grid size={6}>
              <Item>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  fontWeight="bold"
                  marginRight={1}
                >
                  ID:
                </Typography>
                <Typography>{user.id}</Typography>
              </Item>
            </Grid>
            <Grid size={6}>
              <Item>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  fontWeight="bold"
                  marginRight={1}
                >
                  Email:
                </Typography>
                <Typography>{user.email}</Typography>
              </Item>
            </Grid>
            <Grid size={6}>
              <Item>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  fontWeight="bold"
                  marginRight={1}
                >
                  Phone Number:
                </Typography>
                <Typography>{user.phoneNumber}</Typography>
              </Item>
            </Grid>
            <Grid size={6}>
              <Item>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  fontWeight="bold"
                  marginRight={1}
                >
                  Enabled:
                </Typography>
                <Typography>{user.enabled ? "Yes" : "No"}</Typography>
              </Item>
            </Grid>
            <Grid size={6}>
              <Item>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  fontWeight="bold"
                  marginRight={1}
                >
                  Created At:
                </Typography>
                <Typography>
                  {user.createdAt
                    ? format(new Date(user.createdAt), "MM/dd/yyyy hh:mm a")
                    : ""}
                </Typography>
              </Item>
            </Grid>
            <Grid size={6}>
              <Item>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  fontWeight="bold"
                  marginRight={1}
                >
                  Updated At:
                </Typography>
                <Typography>
                  {user.updatedAt
                    ? format(new Date(user.updatedAt), "MM/dd/yyyy hh:mm a")
                    : ""}
                </Typography>
              </Item>
            </Grid>
            <Grid size={6}>
              <Item>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  fontWeight="bold"
                  marginRight={1}
                >
                  Identity Verified:
                </Typography>
                {user.identityVerified ? (
                  <CheckCircleOutlineIcon color={"success"} />
                ) : (
                  <HighlightOffIcon color={"error"} />
                )}
              </Item>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>
      <Box display="flex" justifyContent={"start"} mt={"16px"}>
        <Button variant={"contained"} onClick={onSetIdentityVerifiedClick}>
          {user.identityVerified
            ? "Set user identity unverified"
            : "Set user identity verified"}
          <LoadingIndicator isVisible={updateUserState.isLoading} />
        </Button>
        {updateUserState.isError && (
          <Typography color="error">
            Error: {getErrorMessage(updateUserState.error)}
          </Typography>
        )}
      </Box>

      <Box mt="12px">
        <Typography variant={"h5"}>Verification Images</Typography>
        <ImageUploadInput
          purpose={ImagePurpose.USER_VALIDATION}
          entity={ImageEntity.USER}
          userId={userId}
          onUploadComplete={(fileId) => setNewFileIds([...newFileIds, fileId])}
          withPreview={false}
          buttonText="Add Image"
        />
        <Box display="flex" flexDirection={"row"}>
          {user.validationImages?.map((vi) => (
            <Box sx={{ width: "255px", height: "250px" }}>
              <Box
                key={vi.fileId}
                sx={{
                  width: "250px",
                  height: "250px",
                  backgroundImage: `url("${vi.imageUrl}")`,
                  backgroundSize: "contain",
                }}
              />
              <LoadingIndicator
                isVisible={deleteValidationImageState.isLoading}
              />
              {deleteValidationImageState.isError && (
                <Typography>
                  {getErrorMessage(deleteValidationImageState.error)}
                </Typography>
              )}
              <DeleteIcon onClick={getOnDeleteImageClick(vi.fileId)} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default UserDetails;
